import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ClipboardCheckIcon, InboxIcon, XIcon } from 'lucide-react';
import type { Navigate, PtoRequest } from '../../types/hr';
import { getEmployee, displayName } from '../../utils/employees';
import { formatDays, formatRange, relativeDay } from '../../utils/format';
import { policyName } from '../../utils/timeOff';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
interface PendingRequestsCardProps {
  requests: PtoRequest[];
  onDecide: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
export function PendingRequestsCard({
  requests,
  onDecide,
  onNavigate
}: PendingRequestsCardProps) {
  const pending = requests.filter(r => r.status === 'pending');
  return <Card title="Pending PTO requests" subtitle={pending.length ? `${pending.length} awaiting your decision` : 'Queue is clear'} icon={ClipboardCheckIcon} action={<Button variant="ghost" size="sm" onClick={() => onNavigate({
    page: 'timeoff',
    tab: 'requests'
  })} data-id="element-27" data-mp="1mh4abc9bee6n">
          Open queue
        </Button>} bodyClassName="px-3 pb-3" data-id="element-26" data-mp="3a27r2grfqr7b">
      {pending.length === 0 ? <div className="px-3 pb-3" data-id="element-28" data-mp="1dfwmxp8c6xrc">
          <EmptyState icon={InboxIcon} title="All caught up" description="New requests will appear here as soon as they're submitted." compact data-id="element-29" data-mp="214xpivuhneop" />
        </div> : <ul className="divide-y divide-slate-100" data-id="element-30" data-mp="255rc9lw72ng0">
          <AnimatePresence initial={false} data-id="element-31" data-mp="1rfnvvbjzxnyh">
            {pending.map(r => {
          const emp = getEmployee(r.employeeId);
          if (!emp) return null;
          const after = r.balanceBefore - r.days;
          return <motion.li key={r.id} layout initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0,
            x: 24
          }} transition={{
            duration: 0.2
          }} className="flex flex-col gap-3 rounded-xl px-3 py-3.5 transition hover:bg-canvas/70 sm:flex-row sm:items-center" data-id="element-32" data-mp="2k1qjyvfe6lkm">
                  <button type="button" onClick={() => onNavigate({
              page: 'profile',
              employeeId: emp.id,
              tab: 'timeoff'
            })} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-id="element-33" data-mp="2ou5r3fkpc0ga">
                    <Avatar name={displayName(emp)} seed={emp.id} data-id="element-34" data-mp="2azd2zsqqxby3" />
                    <span className="min-w-0" data-id="element-35" data-mp="2611srgwjgzfc">
                      <span className="flex flex-wrap items-center gap-2" data-id="element-36" data-mp="2624lzvhc3dyf">
                        <span className="text-sm font-semibold text-navy" data-id="element-37" data-mp="2142xuw51wog3">{displayName(emp)}</span>
                        <Badge tone={r.policyId === 'newhire' ? 'navy' : 'blue'} data-id="element-38" data-mp="1w2dp5n5awmv6">{policyName(r.policyId)}</Badge>
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-600" data-id="element-39" data-mp="1s4g0clfg02sb">
                        {formatRange(r.start, r.end)} · {formatDays(r.days)} ·{' '}
                        <span className={after < 0 ? 'font-medium text-rose-600' : 'text-muted'} data-id="element-40" data-mp="bk1q5r3i1gva">{after} days after</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs italic text-muted" data-id="element-41" data-mp="ljk9c5bl4yma">“{r.note}” · {relativeDay(r.submitted)}</span>
                    </span>
                  </button>
                  <div className="flex shrink-0 gap-2 pl-[52px] sm:pl-0" data-id="element-42" data-mp="3jzlimnwciv9a">
                    <Button size="sm" variant="secondary" icon={XIcon} onClick={() => onDecide(r.id, 'denied')} data-id="element-43" data-mp="3p0smwcza5cb7">
                      Deny
                    </Button>
                    <Button size="sm" variant="primary" icon={CheckIcon} onClick={() => onDecide(r.id, 'approved')} data-id="element-44" data-mp="2lzt1mmvz8da">
                      Approve
                    </Button>
                  </div>
                </motion.li>;
        })}
          </AnimatePresence>
        </ul>}
    </Card>;
}